import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Box,
  Typography,
  Switch,
  Tooltip,
  Checkbox
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { capturePHEvents } from '../analytics/posthog.analytics';

import { PH_CLASS_UPDATED, PH_NEW_CLASS_ADDED } from '../utils/posthog.eventNames';
import TutorIcon from '../svg/TutorIcon.svg';
import AcademicIcon from '../svg/AcademicIcon.svg';
import HobbyIcon from '../svg/HobbyIcon.svg';
// import CertificationIcon from '../svg/CertificationIcon.svg';
import ImageCheckbox from './ImageCheckbox';
import { fetchClassSubjects, getAllBannerAssets } from '../service/cms.service';
import { createClassForOrg, updateClass } from '../service/class.service';
import ClassBannerSelect from './classBanner/ClassBannerSelect';
import Iconify from '../fl-components/Iconify';
import { ADD_CLASS_SUBJECT, ASSIGN_ASSETS } from '../redux/constants';
import { uploadFile } from '../service/upload.file.service';
import ClassTimingBox from './classAddModal/classTimingBox';
import Modal from './common/Modal';

export default function AddClassPopup({
  isOpen,
  handleClosePopup,
  handleAddNewClass,
  editObj,
  handleClassUpdate
}) {
  // == Track Fields state to create new classroom ==
  const orgList = useSelector((state) => state.org.orgDetails);
  const classSubject = useSelector((state) => state.classes.classSubject);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const assetsArray = useSelector((state) => state.bannerAssets.assetsArray);
  const dispatch = useDispatch();
  const [selectedOrg, setOrg] = useState(orgList[0]);
  const [className, setClassName] = useState('');
  const [subject, setSubject] = useState(null);
  const [weeksList, setWeeksList] = useState([
    {
      _id: '1',
      day: 'Monday',
      selected: false,
      slots: []
    },
    {
      _id: '2',
      day: 'Tuesday',
      selected: false,
      slots: []
    },
    {
      _id: '3',
      day: 'Wednesday',
      selected: false,
      slots: []
    },
    {
      _id: '4',
      day: 'Thursday',
      selected: false,
      slots: []
    },
    {
      _id: '5',
      day: 'Friday',
      selected: false,
      slots: []
    },
    {
      _id: '6',
      day: 'Saturday',
      selected: false,
      slots: []
    },
    {
      _id: '7',
      day: 'Sunday',
      selected: false,
      slots: []
    }
  ]);
  const [classDescription, setClassDescription] = useState('');
  const [classType, setClassType] = useState('free');
  const [selectMode, setSelectMode] = useState('Monthly');
  const [amount, setAmount] = useState(0);
  const [appUse, setAppUse] = useState('');
  const [isBtnLoading, setBtnLoading] = useState(false);
  // Banner view
  const [displayBanner, setDisplayBanner] = useState(false);
  // Discoverable state
  const [isDiscoverable, setIsDiscoverable] = useState(false);
  // Banner image url
  const [bannerData, setBannerData] = useState('');
  // Banner image generating
  const [base64Generating, setBase64Generation] = useState(false);
  // Use default banner
  const [useDefaultBanner, setDefaultBanner] = useState(true);

  // == Track Fields error state ==
  const [classNameError, setClassNameError] = useState(false);
  const [subjectError, setSubjectError] = useState(false);
  const [classTimingError, setClassTimingError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [orgError, setOrgError] = useState(false);

  useEffect(() => {
    async function getClassSubject() {
      const subjectRes = await fetchClassSubjects();
      if (subjectRes) {
        // setClassSubList(subjectRes);
        dispatch({ type: ADD_CLASS_SUBJECT, payload: subjectRes });
      }
    }
    if (classSubject.length === 0) {
      getClassSubject();
    }
  }, [classSubject]);

  useEffect(() => {
    const fetchAssets = async () => {
      getAllBannerAssets().then((res) => {
        dispatch({ type: ASSIGN_ASSETS, payload: res });
      });
    };
    if (assetsArray === null) fetchAssets();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setClassName('');
      setSubject([]);
      setWeeksList([]);
      // setClassTiming([]);
      setClassDescription('');
      setClassType('free');
      setAmount(0);
      setAppUse('');
      setSelectMode('Monthly');
      setBtnLoading(false);
      setClassNameError(false);
      setSubjectError(false);
      setClassTimingError(false);
      setCategoryError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (editObj) {
      const findIndex = classSubject.findIndex((clas) => clas.name === editObj.subject);
      const findOrgIndex = orgList.findIndex((org) => org.orgId === editObj.orgId);
      setClassName(editObj.name);
      setSubject(findIndex > -1 ? classSubject[findIndex] : []);
      setOrg(orgList[findOrgIndex]);
      setWeeksList(editObj.schedule);
      setClassDescription(editObj?.description ?? '');
      setClassType(editObj.type);
      setAppUse(editObj.category);
      setIsDiscoverable(editObj.isDiscoverable);
    }
  }, [editObj, classSubject]);

  /**
   * Check for all field validation
   * @returns {boolean} a flag that determines fields are valid
   */
  const validateFields = () => {
    let isAllFieldValid = true;

    // Validate class name
    if (className.trim() === '') {
      isAllFieldValid = false;
      setClassNameError(true);
    } else {
      setClassNameError(false);
    }

    // Validate subject
    if (subject === null) {
      isAllFieldValid = false;
      setSubjectError(true);
    }

    // Validate timings
    if (weeksList.filter((wl) => wl.selected).length === 0) {
      isAllFieldValid = false;
      setClassTimingError(true);
    } else setClassTimingError(false);

    if (!appUse.length) {
      isAllFieldValid = false;
      setCategoryError(true);
    } else {
      setCategoryError(false);
    }

    if (selectedOrg === null && editObj) {
      isAllFieldValid = false;
      setOrgError(true);
    } else {
      setOrgError(false);
    }

    return isAllFieldValid;
  };

  // == Add new classroom handler ==
  const handleAddClassroom = async () => {
    if (validateFields()) {
      setBtnLoading(true);
      const newSchedule = weeksList.map((week) => ({
        day: week.day,
        selected: week.selected,
        slots: week.slots.map((slot) => ({
          from: slot.from,
          to: slot.to
        }))
      }));
      const newClassObj = {
        name: className.trim(),
        subject: subject.name,
        schedule: newSchedule,
        type: classType,
        category: appUse,
        isPaid: classType !== 'free',
        isDiscoverable
      };
      if (classDescription) newClassObj.description = classDescription.trim();

      // If class is discoverable then upload banner image and add to class obj;
      // if (isDiscoverable) {
      // User selected personal banner
      const fileObj = {
        base64: bannerData.imageBaseURL,
        fileType: bannerData.type,
        fileName: bannerData.name,
        uploadFor: 'classBanner',
        userId: currentUser.id,
        orgId: selectedOrg?.id ?? selectedOrg?.orgId
      };

      if (editObj) {
        if (!useDefaultBanner) {
          const res = await uploadFile('', fileObj);
          if (res) newClassObj.bannerUrl = res.fileURL;
        }
      } else {
        const res = await uploadFile('', fileObj);
        if (res) newClassObj.bannerUrl = res.fileURL;
      }
      // }

      if (editObj) {
        const updateRes = await updateClass(editObj?.id ?? editObj?._id, newClassObj);
        if (updateRes) {
          handleClassUpdate(updateRes);
          capturePHEvents(PH_CLASS_UPDATED, { classId: editObj.id, className: newClassObj.name });
          setBtnLoading(false);
          handleClosePopup();
        } else {
          setBtnLoading(false);
        }
      } else {
        newClassObj.orgId = selectedOrg?.id ?? selectedOrg?.orgId;
        const res = await createClassForOrg(newClassObj);
        if (res) {
          setBtnLoading(false);
          res.studentCount = 0;
          res.orgId = {
            name: selectedOrg.name,
            id: selectedOrg?.id ?? selectedOrg?.orgId
          };
          capturePHEvents(PH_NEW_CLASS_ADDED, { classId: res.id, className: res.name });
          handleAddNewClass(res);
        } else {
          setBtnLoading(false);
        }
      }
    }
  };

  // <Dialog  fullWidth aria-labelledby="alert-dialog-title">

  //       <>
  //         <Button
  //           onClick={displayBanner ? () => setDisplayBanner(false) : () => handleClosePopup()}
  //           disabled={isBtnLoading}
  //         >
  //           {displayBanner ? 'Back' : 'Cancel'}
  //         </Button>
  //         {displayBanner || (useDefaultBanner && editObj) ? (
  //           <LoadingButton
  //             variant="contained"
  //             onClick={handleAddClassroom}
  //             disabled={base64Generating || isBtnLoading}
  //             autoFocus
  //             loading={isBtnLoading}
  //           >
  //             {editObj ? 'Update' : 'Add'}
  //           </LoadingButton>
  //         ) : (
  //           <Button
  //             variant="contained"
  //             onClick={() => {
  //               if (validateFields()) {
  //                 setDisplayBanner(true);
  //               }
  //             }}
  //           >
  //             Next
  //           </Button>
  //         )}
  //       </>
  //   </Dialog>
  return (
    <Modal
      open={isOpen}
      onClose={handleClosePopup}
      title="Add New Classroom"
      onCancel={displayBanner ? () => setDisplayBanner(false) : () => handleClosePopup()}
      isCancelDisabled={isBtnLoading}
      isBtnLoading={isBtnLoading}
      cancelText={displayBanner ? 'Back' : 'Cancel'}
      actionText={
        // eslint-disable-next-line no-nested-ternary
        displayBanner || (useDefaultBanner && editObj !== null)
          ? editObj
            ? 'Update'
            : 'Add'
          : 'Next'
      }
      onAction={
        displayBanner || (useDefaultBanner && editObj !== null)
          ? () => handleAddClassroom()
          : () => {
              if (validateFields()) {
                setDisplayBanner(true);
              }
            }
      }
      isActionDisabled={base64Generating || isBtnLoading}
      sx={{
        '.MuiDialogContent-root': {
          paddingTop: '0 !important'
        }
      }}
    >
      {displayBanner ? (
        <ClassBannerSelect
          className={className}
          onImageUpdate={(imageData) => setBannerData(imageData)}
          setBase64Generation={setBase64Generation}
          base64Generating={base64Generating}
          isDiscoverable={isDiscoverable}
        />
      ) : (
        <>
          {/* Class Name */}
          <TextField
            autoFocus
            sx={{ mb: '4px', mt: '10px' }}
            error={classNameError}
            label="Enter Class Name"
            type="text"
            fullWidth
            name="className"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            helperText={classNameError && !className ? 'Class name is required' : ''}
            inputProps={{
              maxLength: 50
            }}
          />
          {!editObj && (
            <Autocomplete
              value={selectedOrg}
              options={orgList}
              disabled={orgList.length === 1}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) => setOrg(value ?? null)}
              renderInput={(params) => (
                <TextField
                  error={orgError && selectedOrg === null}
                  helperText={orgError && selectedOrg === null ? 'Please select organization' : ''}
                  margin="dense"
                  {...params}
                  variant="outlined"
                  label="Select Organization"
                />
              )}
            />
          )}
          {/* Subjects List */}
          <Autocomplete
            value={subject}
            options={classSubject}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) => setSubject(value)}
            renderInput={(params) => (
              <TextField
                error={subjectError && subject === null}
                helperText={subjectError && subject === null ? 'Please select subject' : ''}
                margin="dense"
                {...params}
                variant="outlined"
                label="Select Subject"
              />
            )}
          />
          {/* Class Timings */}
          <ClassTimingBox
            weeksList={weeksList}
            setWeeksList={setWeeksList}
            error={classTimingError}
          />

          {/* Class Description */}
          <TextField
            margin="dense"
            label="About Class"
            type="text"
            fullWidth
            multiline
            rows={5}
            inputProps={{
              maxLength: 100
            }}
            name="classDescription"
            value={classDescription}
            onChange={(e) => setClassDescription(e.target.value)}
          />
          <div
            style={{
              textAlign: 'end',
              color: `${classDescription.length <= 100 ? '#919EAB' : '#fb514d'}`
            }}
          >
            {`Character ${classDescription.length}/100`}
          </div>
          {/* Class Types */}
          <FormControl margin="dense" sx={{ mt: '5px' }}>
            <FormLabel id="demo-form-control-label-placement">Class Type</FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-form-control-label-placement"
              name="position"
              defaultValue="top"
              value={classType}
              onChange={(e) => setClassType(e.target.value)}
            >
              <FormControlLabel
                value="free"
                control={<Radio />}
                label="Free"
                labelPlacement="right"
              />
              <FormControlLabel
                value="paid"
                control={<Radio />}
                label="Paid"
                labelPlacement="right"
              />
            </RadioGroup>
          </FormControl>
          {classType !== 'free' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Payment Mode Dropdown */}
              <FormControl sx={{ flex: '1', marginRight: '10px' }}>
                <InputLabel id="payment-mode">Select mode</InputLabel>
                <Select
                  labelId="payment-mode"
                  label="Select mode"
                  value={selectMode}
                  onChange={(e) => setSelectMode(e.target.value)}
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="One Time Payment">One Time Payment</MenuItem>
                </Select>
              </FormControl>
              {/* Enter amount Field */}
              <TextField
                sx={{ flex: '1' }}
                margin="dense"
                label="Enter amount"
                type="text"
                name="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Box>
          )}
          {/* Image Checkbox */}
          <p style={{ margin: '5px 0 10px' }}>You want to use Fainlearn for</p>
          {classType !== 'certification' && (
            <Grid container spacing={2} sx={{ marginBottom: '15px' }}>
              <Grid item xs={12} sm={6} md={6}>
                <ImageCheckbox
                  title="Tutor"
                  icon={TutorIcon}
                  appUse={appUse}
                  setAppUse={setAppUse}
                  checkboxName="tutor"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <ImageCheckbox
                  title="Academic"
                  icon={AcademicIcon}
                  appUse={appUse}
                  setAppUse={setAppUse}
                  checkboxName="academic"
                />
              </Grid>
            </Grid>
          )}
          <Grid container spacing={2}>
            {classType !== 'certification' && (
              <Grid item xs={12} sm={6} md={6}>
                <ImageCheckbox
                  title="Hobby"
                  icon={HobbyIcon}
                  appUse={appUse}
                  setAppUse={setAppUse}
                  checkboxName="hobby"
                />
              </Grid>
            )}
          </Grid>
          {categoryError && <Typography sx={{ color: 'red' }}>Please select category</Typography>}

          <FormControlLabel
            sx={{ mt: 2, ml: 0, width: '100%', justifyContent: 'start' }}
            control={
              <Switch
                color="primary"
                checked={isDiscoverable}
                onChange={(e) => setIsDiscoverable(e.target.checked)}
              />
            }
            label={
              <Typography alignItems="center" display="flex" gap={0.5}>
                Make your class public
                <Tooltip
                  title="If you want to make class to display all the student."
                  placement="top"
                  arrow
                >
                  <Box component="span" display="flex">
                    <Iconify icon="bi:info-circle-fill" />
                  </Box>
                </Tooltip>
              </Typography>
            }
            labelPlacement="start"
          />

          {isDiscoverable && editObj && (
            <FormControlLabel
              sx={{ mt: { xs: 0, md: 1 }, ml: 0 }}
              control={
                <Checkbox
                  checked={useDefaultBanner}
                  onChange={(e) => setDefaultBanner(e.target.checked)}
                />
              }
              label="Use current banner image"
              labelPlacement="start"
            />
          )}
        </>
      )}
    </Modal>
  );
}

AddClassPopup.defaultProps = {
  editObj: null
};

AddClassPopup.propTypes = {
  isOpen: PropTypes.bool,
  handleClosePopup: PropTypes.func,
  handleAddNewClass: PropTypes.func,
  handleClassUpdate: PropTypes.func,
  editObj: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    id: PropTypes.string,
    category: PropTypes.string,
    subject: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    schedule: PropTypes.arrayOf(
      PropTypes.shape({
        day: PropTypes.string,
        selected: PropTypes.bool,
        slots: PropTypes.arrayOf(
          PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
          })
        )
      })
    ),
    isDiscoverable: PropTypes.bool
  })
};
